import { useState } from "react";
import { Cancel01Icon } from "@hugeicons-pro/core-stroke-rounded";
import {
  Button,
  ButtonSize,
  ButtonVariant,
  IconButton,
  IconButtonSize,
  IconButtonVariant,
  Modal
} from "shared/components";
import { BaseInput } from "shared/components/forms/BaseInput";
import { validateFallbackText } from "views/settings-communication/lib/variable-token";

interface VariableFallbackModalProps {
  opened: boolean;
  label: string;
  initialValue?: string;
  // "insert" (default): variable is being added to the template for the
  // first time. "edit": variable is already in the template; only its
  // fallback text is being changed.
  mode?: "insert" | "edit";
  onConfirm: (fallback: string) => void;
  onClose: () => void;
}

// The modal component stays mounted across opens (callers just flip
// `opened`), so the draft-resetting content is keyed by `opened` here —
// remounting fresh on every open instead of syncing state via an effect.
export const VariableFallbackModal = ({
  opened,
  label,
  initialValue = "",
  mode = "insert",
  onConfirm,
  onClose
}: VariableFallbackModalProps) => (
  <Modal
    opened={opened}
    onClose={onClose}
    panelClassName="max-w-[480px] w-full"
    zIndex={60}
  >
    <VariableFallbackModalContent
      key={opened ? "open" : "closed"}
      label={label}
      initialValue={initialValue}
      mode={mode}
      onConfirm={onConfirm}
      onClose={onClose}
    />
  </Modal>
);

const VariableFallbackModalContent = ({
  label,
  initialValue,
  mode,
  onConfirm,
  onClose
}: Omit<VariableFallbackModalProps, "opened" | "initialValue"> & {
  initialValue: string;
}) => {
  const [value, setValue] = useState(initialValue);
  const [error, setError] = useState("");

  const handleConfirm = () => {
    const validationError = validateFallbackText(value);
    if (validationError) {
      setError(validationError);
      return;
    }
    onConfirm(value.trim());
  };

  return (
    <>
      <div className="flex items-center justify-between px-6 py-3 border-b border-alpha-100">
        <h2 className="text-xl font-bold text-content-1000">
          {mode === "edit"
            ? "Edit variable fallback"
            : "Provide variable fallback"}
        </h2>
        <IconButton
          variant={IconButtonVariant.BASE}
          size={IconButtonSize.SM}
          onClick={onClose}
          HugeIcon={Cancel01Icon}
          borderLess
        />
      </div>

      <div className="px-6 py-6 space-y-2">
        <p className="text-sm text-content-500">
          This text will be used if {label} isn&apos;t available for a
          recipient.
        </p>
        <BaseInput
          id="variable-fallback-input"
          label="Fallback text"
          required
          value={value}
          onChange={(next: string) => {
            setValue(next);
            if (error) setError("");
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              handleConfirm();
            }
          }}
          placeholder="e.g. our team"
          className="w-full"
          autoFocus
          error={error || undefined}
        />
      </div>

      <div className="flex justify-end gap-2 px-6 py-4 border-t border-alpha-100">
        <Button
          size={ButtonSize.BASE}
          variant={ButtonVariant.BASE}
          onClick={onClose}
          type="button"
        >
          Cancel
        </Button>
        <Button
          size={ButtonSize.BASE}
          variant={ButtonVariant.PRIMARY}
          onClick={handleConfirm}
          type="button"
        >
          {mode === "edit" ? "Save" : "Insert variable"}
        </Button>
      </div>
    </>
  );
};
